/**
 * Shared marketing-page atoms (PR #51).
 * Restraint > decoration. One accent. Big type. Generous whitespace.
 */
import * as React from "react";
import { cn } from "@/lib/utils";

/** Section wrapper — consistent vertical rhythm and max-width. */
export function MSection({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) {
  return (
    <section className={cn("border-b border-[hsl(var(--rd-border))]/60", className)} {...props}>
      <div className="mx-auto w-full max-w-7xl px-6 py-20 md:py-28">{children}</div>
    </section>
  );
}

/** Eyebrow label above a section/page title. */
export function MEyebrow({ children, className }: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        "text-[12px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--rd-accent-500))]",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Display headline — Linear's hero size, controlled measure. */
export function MTitle({
  children,
  className,
  as: As = "h1",
}: React.PropsWithChildren<{ className?: string; as?: React.ElementType }>) {
  return (
    <As
      className={cn(
        "max-w-4xl text-[44px] font-semibold leading-[1.05] tracking-tight text-[hsl(var(--rd-text-1))] md:text-[64px]",
        className
      )}
    >
      {children}
    </As>
  );
}

/** Section heading — smaller than hero, used for in-page H2s. */
export function MSectionTitle({
  children,
  className,
  as: As = "h2",
}: React.PropsWithChildren<{ className?: string; as?: React.ElementType }>) {
  return (
    <As
      className={cn(
        "max-w-3xl text-[32px] font-semibold leading-[1.15] tracking-tight text-[hsl(var(--rd-text-1))] md:text-[40px]",
        className
      )}
    >
      {children}
    </As>
  );
}

/** Body lede — slightly larger than body, muted. */
export function MLede({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <p
      className={cn(
        "max-w-2xl text-[17px] leading-relaxed text-[hsl(var(--rd-text-2))] md:text-[19px]",
        className
      )}
    >
      {children}
    </p>
  );
}

/** Body paragraph for long-form content (legal, docs). */
export function MProse({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        "prose prose-invert max-w-none text-[15px] leading-7 text-[hsl(var(--rd-text-2))] [&>h2]:mt-10 [&>h2]:text-[20px] [&>h2]:font-semibold [&>h2]:text-[hsl(var(--rd-text-1))] [&>h3]:mt-6 [&>h3]:text-[16px] [&>h3]:font-semibold [&>h3]:text-[hsl(var(--rd-text-1))] [&>p]:my-4 [&>ul]:my-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:my-4 [&>ol]:list-decimal [&>ol]:pl-6 [&>a]:text-[hsl(var(--rd-accent-500))] [&>a]:underline-offset-2 hover:[&>a]:underline",
        className
      )}
    >
      {children}
    </div>
  );
}

/** A bordered card used for feature grids / pricing tiers / case-study cells. */
export function MCard({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-[12px] border border-[hsl(var(--rd-border))] bg-[hsl(var(--rd-surface-1))] p-6",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
